/**
 * Inspection rounds, carried to the platform.
 *
 * Two boards walk rounds, and the platform has a checklist for the airfield
 * only: the airports pack's movement area round, `oema_field_round`.
 *
 *   - The airfield inspection board walks it topic for topic — its twenty-one
 *     topics are the checklist's items, key for key.
 *   - The Inspection platform's area rounds walk an airfield scope area by
 *     area, and its twenty-two topics are the same items (joints and pavement
 *     both answer "pavement"). So an airfield area round is the same
 *     inspection, with one answer per area walked.
 *   - Its buildings and landside scopes are filed against checklists of their
 *     own (build 7: `oema_building_round`, `oema_landside_round`), whose items
 *     are the scopes' topic keys. A scope with no checklist is refused with
 *     that reason — said on the round and in the shell — and stays on screen.
 *   - The Systems board's daily utility check is filed against
 *     `oema_utility_check`, answering the one system checked (build 7).
 *
 * A round goes whole, in one request: every item answered pass, find or skip
 * (an item nobody reached is "not inspected on this round", never a pass), the
 * board's own detail kept with each answer, completed, and a work order raised
 * for each finding at the priority its severity implies. The boards take no
 * photographs, so the round is closed without the photographs the pack asks
 * for, and the platform's record says which findings lack one.
 *
 * Each board raises its own copy of the work at once, as it always did. Those
 * copies are stamped here as the round goes, so the register's refill keeps
 * them until the platform's own orders are listed in their place.
 *
 * Nothing here imports the demo domain: a board hands over its topic, area and
 * severity tables with the round.
 */
import { hasPermission, PERMISSION } from "../../../api/dataSource";
import { roundKeys } from "../../../api/inspectionsQueries";
import {
  FIELD_ROUND,
  type InspectionRoundInput,
  type InspectionRoundResult,
  type RoundAnswerInput,
  type RoundWorkInput,
} from "../../../domain/inspectionRounds";
import {
  BUILDING_ITEMS,
  BUILDING_ROUND,
  LANDSIDE_ITEMS,
  LANDSIDE_ROUND,
  UTILITY_CHECK,
  utilityItem,
} from "../../../domain/boardChecklists";
import { t, tn } from "../../../i18n";
import { registerWrite, type WriteAck, type WriteContext } from "../writes";

/* data on the platform's record, so English as the record's language */
export const NOT_INSPECTED = "Not inspected on this round.";
export const MARKED_NA = "Marked not applicable on the round.";

/** A board's severity band: `AI_SEV` or `BI_SEV`. */
export interface Severity {
  k: string;
  pri: string;
  days: number;
}

/** A work order a board raised for a finding. */
export interface LocalOrder {
  id: string;
  platform?: Record<string, unknown>;
}

/** A board's own work order, and the answer (by client id) it was raised for. */
interface Pair {
  order: LocalOrder;
  observation: string;
}

const clip = (s: string, n: number): string => (s.length > n ? `${s.slice(0, n - 1)}…` : s);
const sentence = (parts: (string | undefined | false)[]): string =>
  parts
    .map((p) => (p || "").trim())
    .filter(Boolean)
    .join(" ");

/** A severity → the platform's priority (1–5) and hours to attend. */
export function workTerms(sevs: Severity[] | undefined, k: string | undefined): { priority: number; slaHours: number } {
  const list = sevs ?? [];
  /* the boards' own default: the second band, as they raise the work */
  const sv = list.find((s) => s.k === k) ?? list[1];
  const p = sv ? Number(String(sv.pri).replace(/\D/g, "")) : 3;
  const days = sv ? Number(sv.days) : 14;
  return {
    priority: Math.min(5, Math.max(1, Number.isFinite(p) && p > 0 ? p : 3)),
    /* "now" is the pack's own "immediate": four hours */
    slaHours: Number.isFinite(days) && days > 0 ? Math.round(days * 24) : 4,
  };
}

/** Say on each of a board's own work orders how the platform took it. */
export function stampOrders(pairs: Pair[], key: string, result: InspectionRoundResult): void {
  for (const { order, observation } of pairs) {
    const w = observation ? result.workOrders.find((x) => x.observation === observation) : undefined;
    order.platform = w
      ? { state: "saved", key, id: w.id, reference: w.number, number: w.number, status: w.status }
      : { state: "refused", key, why: t("The platform did not raise this one."), number: "—", status: "not saved" };
  }
}

const refuseOrders = (pairs: Pair[], key: string, why: string) => {
  for (const { order } of pairs) order.platform = { state: "refused", key, why, number: "—", status: "not saved" };
};

export function roundAck(result: InspectionRoundResult): WriteAck {
  const n = result.workOrders.length;
  const m = result.missingEvidence.length;
  const text = sentence([
    t("Round {ref} saved on the platform.", { ref: result.reference }),
    n > 0 &&
      `${tn(n, "{n} work order raised:", "{n} work orders raised:")} ${result.workOrders.map((w) => w.number).join(", ")}.`,
    m > 0 && tn(m, "{n} finding has no photo.", "{n} findings have no photo."),
  ]);
  return { id: result.id, reference: result.reference, at: result.completedAt, kind: "inspection", text };
}

/** Send one round; the board's own work orders are stamped whatever happens. */
async function sendRound(
  ctx: WriteContext,
  rec: { photosMissing?: number },
  pairs: Pair[],
  body: () => InspectionRoundInput,
): Promise<WriteAck> {
  for (const { order } of pairs) order.platform = { state: "saving", key: ctx.key, number: "—", status: "saving" };
  try {
    /* checked here rather than by the shell, so the board's own work orders are told too */
    if (!hasPermission(ctx.principal, PERMISSION.inspectionsWrite))
      throw new Error(t("Not permitted: your role does not hold {p}.", { p: PERMISSION.inspectionsWrite }));
    const result = await ctx.source.recordInspectionRound(body(), ctx.key);
    rec.photosMissing = result.missingEvidence.length;
    stampOrders(pairs, ctx.key, result);
    return roundAck(result);
  } catch (e) {
    refuseOrders(pairs, ctx.key, e instanceof Error ? e.message : String(e));
    throw e;
  }
}

function pairsFor<L extends { wo?: string }>(
  orders: LocalOrder[] | undefined,
  lines: L[],
  observation: (l: L) => string,
): Pair[] {
  return (orders ?? []).map((order) => {
    const l = lines.find((x) => x.wo === order.id);
    return { order, observation: l ? observation(l) : "" };
  });
}

const INVALIDATE = (siteId: string) => [
  [...roundKeys.all(siteId)],
  ["inspections", siteId],
  ["work-orders", siteId],
  ["kpis", siteId],
];

/* ── the airfield inspection board ─────────────────────────────────────── */

/** One topic's line on an airfield round, as the board keeps it. */
export interface AirfieldLine {
  topic: string;
  result: string;
  types?: string[];
  sev?: string;
  where?: string;
  note?: string;
  recurring?: boolean;
  wo?: string;
}

/** An airfield round as the board keeps it (generated.ts `aiRound`). */
export interface AirfieldRound {
  id: string;
  at?: string;
  wx?: string;
  rwy?: string;
  lines?: AirfieldLine[];
  /** findings closed without the photograph the pack asks for, once the platform has the round */
  photosMissing?: number;
}

export interface AirfieldExtra {
  /** the board's own copies of the work it raised */
  orders?: LocalOrder[];
  /** the board's topics (`AI_TOPICS`): the checklist's items, in the board's order */
  topics?: { k: string; label: string }[];
  /** the board's severities (`AI_SEV`) */
  sev?: Severity[];
}

/** What the inspector found, in one line the crew and the next inspector can read. */
export function findingNote(l: AirfieldLine): string {
  return sentence([
    `${(l.types ?? []).join(", ") || "Defect"}.`,
    l.note,
    l.where?.trim() && `Where: ${l.where.trim()}.`,
    l.recurring && "Seen before and getting worse.",
  ]);
}

/** An airfield round as the platform takes it: every topic answered, the work for each finding. */
export function airfieldRoundBody(
  rec: AirfieldRound,
  extra: AirfieldExtra,
  siteId: string,
  key: string,
): InspectionRoundInput {
  const lines = rec.lines ?? [];
  const topics = extra.topics?.length ? extra.topics : lines.map((l) => ({ k: l.topic, label: l.topic }));
  const label = (k: string) => topics.find((x) => x.k === k)?.label ?? k;
  const answers: RoundAnswerInput[] = topics.map((tp) => {
    const l = lines.find((x) => x.topic === tp.k);
    const clientId = `${key}:${tp.k}`;
    if (!l) return { itemKey: tp.k, value: "skip", skippedReason: NOT_INSPECTED, clientId, attrs: { blank: true } };
    if (l.result === "defect")
      return {
        itemKey: tp.k,
        value: "find",
        notes: clip(findingNote(l), 4000),
        clientId,
        attrs: {
          types: l.types ?? [],
          severity: l.sev ?? null,
          where: l.where ?? "",
          note: l.note ?? "",
          recurring: !!l.recurring,
        },
      };
    if (l.result === "pass") return { itemKey: tp.k, value: "pass", clientId };
    return { itemKey: tp.k, value: "skip", skippedReason: MARKED_NA, clientId, attrs: { na: true } };
  });
  const findings = lines.filter((l) => l.result === "defect" && topics.some((x) => x.k === l.topic));
  const context = sentence([
    rec.wx?.trim() && `Conditions: ${rec.wx.trim()}.`,
    rec.rwy?.trim() && `Runway in use: ${rec.rwy.trim()}.`,
  ]);
  const work: RoundWorkInput[] = findings.map((l) => ({
    observation: `${key}:${l.topic}`,
    title: clip(`${label(l.topic)} — ${(l.types ?? []).join(", ") || "defect"}`, 200),
    description: clip(sentence([findingNote(l), context]), 4000),
    ...workTerms(extra.sev, l.sev),
    area: "airside",
  }));
  return {
    siteId,
    template: FIELD_ROUND,
    clientId: key,
    attrs: { board: "airfield", round: rec.id, conditions: rec.wx?.trim() || null, runway: rec.rwy?.trim() || null },
    answers,
    complete: true,
    /* the board takes no photographs: a finding is closed without one, and the record says so */
    allowMissingEvidence: findings.length > 0,
    work,
  };
}

registerWrite("inspections.airfield", {
  invalidate: INVALIDATE,
  /* one request, idempotent on the round's key: trying again cannot file it twice */
  retryable: true,
  send(ctx, rec: AirfieldRound, extra: AirfieldExtra | null) {
    const ex = extra ?? {};
    const pairs = pairsFor(ex.orders, rec.lines ?? [], (l) => `${ctx.key}:${l.topic}`);
    return sendRound(ctx, rec, pairs, () => airfieldRoundBody(rec, ex, ctx.siteId, ctx.key));
  },
});

/* ── the Inspection platform's area rounds ─────────────────────────────── */

/** The airfield scope's topics → the movement area round's items. */
export const AREA_ITEM: Readonly<Record<string, string>> = {
  pave: "pavement",
  joints: "pavement",
  markings: "markings",
  agl: "agl",
  papi: "papi",
  navaid: "navaid",
  signs: "signs",
  friction: "friction",
  rwy: "rwy",
  twy: "twy",
  resa: "resa",
  roads: "roads",
  rff: "rff",
  fod: "fod",
  spill: "spill",
  veg: "veg",
  wildlife: "wildlife",
  drain: "drainage",
  fence: "fence",
  infield: "shoulder",
  wind: "windsock",
  safety: "safety",
};

/** Where a finding is, as the area round records it. */
export interface AreaLocation {
  label?: string;
  E?: number | null;
  N?: number | null;
  plan?: string | null;
  room?: string | null;
  ref?: string;
  pending?: boolean;
}

/** One (area, topic) line on an area round (generated.ts `biRound`). */
export interface AreaLine {
  area: string;
  topic: string;
  result?: string;
  sev?: string;
  /** what exactly, on a defect */
  note?: string;
  /** the inspector's note, on any result */
  obs?: string;
  loc?: AreaLocation;
  wo?: string;
}

export interface AreaRound {
  id: string;
  scope: string;
  at?: string;
  areas?: string[];
  lines?: AreaLine[];
  photosMissing?: number;
}

export interface AreaExtra {
  scope?: string;
  orders?: LocalOrder[];
  areas?: { k: string; label: string }[];
  topics?: { k: string; label: string }[];
  /** the board's severities (`BI_SEV`) */
  sev?: Severity[];
}

/** The board's E/N (UTM 37N) → WGS 84, when the page can work it out. */
export type ToLonLat = (p: { E: number; N: number }) => { lon: number; lat: number };

/**
 * How a scope's topics meet its checklist (build 7). The airfield scope walks
 * the movement area round through `AREA_ITEM`; the buildings and landside
 * scopes have checklists of their own whose item keys are the topics' keys.
 */
interface ScopeChecklist {
  template: string;
  item: Readonly<Record<string, string>>;
  area: "airside" | "terminal" | "landside";
}

const identity = (keys: readonly string[]): Record<string, string> => Object.fromEntries(keys.map((k) => [k, k]));

export const SCOPE_ROUND: Readonly<Record<string, ScopeChecklist>> = {
  airfield: { template: FIELD_ROUND, item: AREA_ITEM, area: "airside" },
  bldg: { template: BUILDING_ROUND, item: identity(BUILDING_ITEMS), area: "terminal" },
  landside: { template: LANDSIDE_ROUND, item: identity(LANDSIDE_ITEMS), area: "landside" },
};

/** An area round as the platform takes it: one answer per area walked, per topic. */
export function areaRoundBody(
  rec: AreaRound,
  extra: AreaExtra,
  siteId: string,
  key: string,
  toLonLat?: ToLonLat,
): InspectionRoundInput {
  const scope = SCOPE_ROUND[extra.scope ?? rec.scope ?? "airfield"];
  if (!scope) throw new Error(t("The platform has no checklist for this round yet. It stays on this screen until you reload."));
  const ITEM = scope.item;
  const walked = new Set(rec.areas ?? []);
  const lines = (rec.lines ?? []).filter((l) => walked.has(l.area) && l.result);
  const strays = [...new Set(lines.filter((l) => !ITEM[l.topic]).map((l) => l.topic))];
  if (strays.length)
    throw new Error(t("The platform's checklist has no item for: {topics}.", { topics: strays.join(", ") }));
  const areaLabel = (k: string) => extra.areas?.find((a) => a.k === k)?.label ?? k;
  const topicLabel = (k: string) => extra.topics?.find((x) => x.k === k)?.label ?? k;
  const answers: RoundAnswerInput[] = lines.map((l) => {
    const clientId = `${key}:${l.area}:${l.topic}`;
    const where = sentence([areaLabel(l.area), l.loc?.label && `· ${l.loc.label}`]);
    const attrs: Record<string, unknown> = {
      area: l.area,
      topic: l.topic,
      where,
      severity: l.result === "defect" ? (l.sev ?? null) : null,
      note: l.note ?? "",
      obs: l.obs ?? "",
      ...(l.loc ? { loc: l.loc } : {}),
    };
    const obs = l.obs?.trim() && `Inspector's note: ${l.obs.trim()}`;
    if (l.result === "defect") {
      const at =
        l.loc && Number.isFinite(l.loc.E) && Number.isFinite(l.loc.N) && toLonLat
          ? toLonLat({ E: l.loc.E!, N: l.loc.N! })
          : undefined;
      return {
        itemKey: ITEM[l.topic],
        value: "find",
        notes: clip(sentence([`${where}: ${topicLabel(l.topic)}.`, l.note, obs]), 4000),
        clientId,
        attrs,
        ...(at && Number.isFinite(at.lon) && Number.isFinite(at.lat) ? { lon: at.lon, lat: at.lat } : {}),
      };
    }
    if (l.result === "pass")
      return {
        itemKey: ITEM[l.topic],
        value: "pass",
        ...(obs ? { notes: clip(`${where}: ${obs}`, 4000) } : {}),
        clientId,
        attrs,
      };
    return {
      itemKey: ITEM[l.topic],
      value: "skip",
      skippedReason: MARKED_NA,
      clientId,
      attrs: { ...attrs, na: true },
    };
  });
  /* every item the round did not reach in any area: not inspected, never a pass */
  const reached = new Set(answers.map((a) => a.itemKey));
  for (const item of new Set(Object.values(ITEM)))
    if (!reached.has(item))
      answers.push({
        itemKey: item,
        value: "skip",
        skippedReason: NOT_INSPECTED,
        clientId: `${key}:${item}`,
        attrs: { blank: true },
      });
  const findings = lines.filter((l) => l.result === "defect");
  const work: RoundWorkInput[] = findings.map((l) => ({
    observation: `${key}:${l.area}:${l.topic}`,
    title: clip(`${topicLabel(l.topic)} — ${areaLabel(l.area)}`, 200),
    description: clip(
      sentence([
        l.note?.trim() ? `${l.note.trim()}.` : "No detail recorded.",
        l.loc?.label ? `Location: ${l.loc.label}.` : "No location recorded.",
        l.obs?.trim() && `Inspector's note: ${l.obs.trim()}`,
      ]),
      4000,
    ),
    ...workTerms(extra.sev, l.sev),
    area: scope.area,
  }));
  return {
    siteId,
    template: scope.template,
    clientId: key,
    attrs: { board: "areas", scope: extra.scope ?? rec.scope, areas: [...walked], round: rec.id },
    answers,
    complete: true,
    allowMissingEvidence: findings.length > 0,
    work,
  };
}

/** A round of a scope the platform has no checklist for: refused with that reason. */
function noChecklist(ctx: WriteContext, rec: AreaRound, extra: AreaExtra | null): never {
  const why = t("The platform has no checklist for this round yet. It stays on this screen until you reload.");
  refuseOrders(
    pairsFor(extra?.orders, rec.lines ?? [], () => ""),
    ctx.key,
    why,
  );
  throw new Error(why);
}

/**
 * Any area round: the airfield scope as the movement area round, the
 * buildings and landside scopes as their own checklists (build 7 — they were
 * refused before, and lived only until a reload).
 */
async function sendAreaRound(ctx: WriteContext, rec: AreaRound, extra: AreaExtra | null): Promise<WriteAck> {
  const ex = extra ?? {};
  if (!SCOPE_ROUND[ex.scope ?? rec.scope ?? ""]) noChecklist(ctx, rec, extra);
  const pairs = pairsFor(ex.orders, rec.lines ?? [], (l) => `${ctx.key}:${l.area}:${l.topic}`);
    let toLonLat: ToLonLat | undefined;
    if ((rec.lines ?? []).some((l) => l.loc && Number.isFinite(l.loc.E)))
      try {
        /* the survey's own fit, in the demo chunk the board has already loaded */
        toLonLat = (await import("../project")).toLonLat;
      } catch {
        /* no position, then: the location's label still travels with the finding */
      }
    return sendRound(ctx, rec, pairs, () => areaRoundBody(rec, ex, ctx.siteId, ctx.key, toLonLat));
}

registerWrite("inspections.areas", { invalidate: INVALIDATE, retryable: true, send: sendAreaRound });

/* the board files its buildings and landside rounds under this kind */
registerWrite("inspections.building", { invalidate: INVALIDATE, retryable: true, send: sendAreaRound });

/* ── build 7: the Systems board's daily utility check ──────────────────── */

/** One check as the board defines it (`UTIL_SYSTEMS[].checks`). */
export interface UtilityCheckDef {
  k: string;
  label: string;
  target?: string;
  unit?: string;
  crit?: boolean;
}

/** What the person entered against one check. */
export interface UtilityAnswer {
  ok?: boolean | null;
  val?: string;
  note?: string;
}

/** The board's record of one system's check on one day (`UTIL_LOG["power|2026-10-12"]`). */
export interface UtilityRecord {
  by?: string;
  at?: string | null;
  checks?: Record<string, UtilityAnswer>;
}

export interface UtilityExtra {
  system?: string;
  /** the board's own day for the check, YYYY-MM-DD */
  day?: string;
  checks?: UtilityCheckDef[];
}

export const READING_ONLY = "A reading was taken but not marked pass or fail.";

/** One system's daily check as the platform takes it: every check of that system answered. */
export function utilityCheckBody(
  rec: UtilityRecord,
  extra: UtilityExtra,
  siteId: string,
  key: string,
): InspectionRoundInput {
  const system = extra.system ?? "";
  const defs = extra.checks ?? [];
  if (!system || !defs.length) throw new Error(t("Nothing to save: the check names no system."));
  const answers: RoundAnswerInput[] = defs.map((c) => {
    const a = rec.checks?.[c.k] ?? {};
    const itemKey = utilityItem(system, c.k);
    const clientId = `${key}:${c.k}`;
    const val = (a.val ?? "").trim();
    const attrs: Record<string, unknown> = {
      check: c.k,
      ...(val ? { value: val } : {}),
      ...(a.note ? { note: a.note } : {}),
    };
    const notes = sentence([
      val && `${c.label}: ${val}${c.unit ? ` ${c.unit}` : ""}.`,
      c.target && `Target ${c.target}.`,
      a.note,
    ]);
    if (a.ok === true)
      return { itemKey, value: "pass", clientId, attrs, ...(notes ? { notes: clip(notes, 4000) } : {}) };
    if (a.ok === false) return { itemKey, value: "fail", clientId, attrs, notes: clip(notes || `${c.label}.`, 4000) };
    return {
      itemKey,
      value: "skip",
      skippedReason: val ? READING_ONLY : NOT_INSPECTED,
      clientId,
      attrs: { ...attrs, ...(val ? {} : { blank: true }) },
    };
  });
  return {
    siteId,
    template: UTILITY_CHECK,
    clientId: key,
    attrs: { board: "utility", system, day: extra.day ?? null },
    answers,
    complete: true,
    allowMissingEvidence: false,
    /* a failed critical check raises its work from the board, not from the round */
    work: [],
  };
}

registerWrite("utility.check", {
  permission: () => PERMISSION.inspectionsWrite,
  invalidate: (siteId) => [[...roundKeys.all(siteId)], ["inspections", siteId]],
  /* one request, idempotent on the check's key */
  retryable: true,
  async send(ctx, rec: UtilityRecord, extra: UtilityExtra | null) {
    const result = await ctx.source.recordInspectionRound(
      utilityCheckBody(rec, extra ?? {}, ctx.siteId, ctx.key),
      ctx.key,
    );
    return {
      id: result.id,
      reference: result.reference,
      at: result.completedAt,
      kind: "inspection",
      text: t("Daily check {ref} saved on the platform.", { ref: result.reference }),
    };
  },
});
